import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HomeIcon from "@mui/icons-material/Home";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { useNavigate, useSearchParams } from "react-router-dom";
import { trackSpotifyPurchase } from "../utils/loadSpotifyAds";

const SuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  React.useEffect(() => {
    trackSpotifyPurchase({
      orderId: searchParams.get("session_id") || undefined,
    });
  }, [searchParams]);

  const handleBackToHome = () => {
    navigate("/");
  };

  const handleContactSupport = () => {
    // Replace with your actual support contact method
    window.location.href = "mailto:info@thebatcage.ca";
  };

  return (
    <Container maxWidth="sm">
      <Paper
        elevation={4}
        sx={{
          my: { xs: 4, sm: 8 },
          p: { xs: 3, sm: 5 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 4,
          background: "linear-gradient(135deg, #f6f8f9 0%, #e5ebee 100%)",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle background pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: "radial-gradient(#e0e7eb 8%, transparent 8%)",
            backgroundSize: "30px 30px",
            opacity: 0.3,
            zIndex: 1,
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            width: "100%",
          }}
        >
          <CheckCircleOutlineIcon
            sx={{
              fontSize: isMobile ? 80 : 120,
              color: "success.main",
              mb: 3,
              animation: "bounce 1s ease infinite",
            }}
          />

          <Typography
            variant={isMobile ? "h4" : "h3"}
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: "text.primary",
              mb: 2,
              letterSpacing: "-0.5px",
            }}
          >
            Payment Successful!
          </Typography>

          <Typography
            variant={isMobile ? "body1" : "h6"}
            color="text.secondary"
            sx={{
              mb: 4,
              px: 2,
              maxWidth: 500,
              mx: "auto",
            }}
          >
            Your purchase is complete. We appreciate your business and hope you
            enjoy our product!
          </Typography>

          <Stack
            spacing={2}
            direction={isMobile ? "column" : "row"}
            sx={{
              width: "100%",
              justifyContent: "center",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<HomeIcon />}
              onClick={handleBackToHome}
              sx={{
                py: 1.5,
                px: 3,
                borderRadius: 3,
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 8px rgba(0,0,0,0.15)",
                },
              }}
            >
              Return to Home
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              startIcon={<SupportAgentIcon />}
              onClick={handleContactSupport}
              sx={{
                py: 1.5,
                px: 3,
                borderRadius: 3,
                fontWeight: 600,
                textTransform: "none",
                borderWidth: 2,
                "&:hover": {
                  borderWidth: 2,
                  transform: "translateY(-2px)",
                },
              }}
            >
              Contact Support
            </Button>
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 3,
              opacity: 0.7,
            }}
          >
            Questions? We're here to help!
          </Typography>
        </Box>
      </Paper>

      {/* Global styles for animation */}
      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </Container>
  );
};

export default SuccessPage;
